import EventHandler from '@pixelunion/events';
import Flickity from 'flickity';
import Vimeo from '@vimeo/player';
import rimg from '@pixelunion/rimg-shopify';
import ImageZoom from './ImageZoom';
import loadYoutubeIframeAPI from '../helpers/youtubeIframeAPIHelper';
import ProductSlideshow from './ProductSlideshow';
import MasonryGrid from './MasonryGrid';
import themeUtils from '../themeUtils';

export default class ProductGallery {
  constructor(el, settings) {
    this.el = el;
    this.initialGallery = this.el.innerHTML;
    this.settings = settings;
    this.layout = this.el.dataset.productGalleryLayout;
    this.productContainer = this.settings.product_container;
    this.media = [];
    this.models = {};
    this.players = [];
    this.flickityEvents = [];
    this.pageDots = null;
    this.flickityNavButtons = null;
    this.externalVideoLoaded = false;
    this.videoLoaded = false;
    this.modelLoaded = false;
    this.productMasonryLayout = null;
    this.flickity = null;

    this.viewport = this.el.querySelector('[data-product-gallery-viewport]');
    this.navigation = this.el.querySelector('[data-product-gallery-navigation]');
    this.masonryContainer = this.el.querySelector('[data-masonry-gallery]');
    this.figures = this.el.querySelectorAll('[data-product-gallery-figure]');

    this.initMasonry = this._initProductMasonry.bind(this);
    this.events = new EventHandler();

    if (this.viewport) {
      this.selected = {
        figure: this.viewport.querySelector('[data-product-gallery-selected="true"]'),
        thumbnail: this.navigation ? this.navigation.querySelector('[data-product-gallery-selected="true"]') : null,
      };
    }

    const features = [];

    if (this.el.querySelector('[data-media-type="model"]')) {
      features.push(
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this._onModelLibraryLoad.bind(this),
        },
      );
      features.push(
        {
          name: 'shopify-xr',
          version: '1.0',
        },
      );
    }

    if (this.el.querySelector('[data-media-type="video"]')) {
      Promise.all([
        new Promise(resolve => {
          if (!document.querySelector('#plyr-stylesheet')) {
            const link = document.createElement('link');

            link.setAttribute('id', 'plyr-stylesheet');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css');
            link.onload = resolve;
            document.body.appendChild(link);
          } else {
            resolve();
          }
        }),
        new Promise(resolve => {
          features.push({
            name: 'video-ui',
            version: '1.0',
            onLoad: resolve,
          });
        }),
      ]).then(this._onVideoLibraryLoad.bind(this));
    }

    loadYoutubeIframeAPI().then(this._onExternalVideoLibraryLoad.bind(this));

    if (features.length) {
      Shopify.loadFeatures(features);
    }

    this._registerEvents();
    this._initLayout();
  }

  _registerEvents() {
    if (this.el.querySelector('[data-media-type="model"]')) {
      this.events.register(this.viewport, 'click', e => {
        if ('shopifyXr' in e.target.dataset) {
          this._onViewInYourSpaceClick(e.target);
        }
      });
    }

    if (this.layout === 'masonry') {
      this.events.register(window, 'resize', (e) => this.initMasonry(e));
    }

    rimg.watch(this.el);
  }

  _initLayout() {
    if (this.layout === 'masonry') {
      this._initProductMasonry();
    } else if (this.layout === 'slideshow') {
      this.slideshow = new ProductSlideshow({ el: this.el });

      if (this.navigation) {
        this.thumbnails = this.navigation.querySelectorAll('[data-product-gallery-thumbnail]');
        this.onThumbnailClick = e => (
          this._selectMediaByIndex(e.currentTarget.dataset.productGalleryThumbnail)
        );

        this.thumbnails.forEach(thumbnail => this.events.register(thumbnail, 'click', this.onThumbnailClick));
      }

      if (this.settings.enable_zoom && this.el.querySelectorAll('[data-media-type="image"]').length > 0) {
        this.zoomView = new ImageZoom({
          viewport: this.viewport,
          images: this.settings.images_json,
        });
      }
    }
  }

  selectMediaByVariant(variant) {
    if (!variant || !variant.featured_media) return;

    if (this.slideshow) {
      const figure = this.viewport.querySelector(`[data-media="${variant.featured_media.id}"]`);
      this.slideshow.selectThumbnail(variant.featured_media.id);
      this._selectMediaByIndex(figure.dataset.productGalleryFigure);
    } else if (this.flickity) {
      const slideIndex = this.el.querySelector(`[data-media="${variant.featured_media.id}"]`).dataset.productGalleryFigure;
      this.flickity.select(parseInt(slideIndex, 10));
    }
  }

  unload() {
    this.events.unregisterAll();

    this.media.forEach(m => m.unload());
    this.media = [];

    if (this.productMasonryLayout) {
      this.productMasonryLayout.unload();
      this.productMasonryLayout = null;
    }

    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
      this.flickityEvents = [];
    }

    if (this.imageZoom) {
      this.imageZoom.unload();
    }

    this.el.innerHTML = this.initialGallery;
  }

  _onModelLibraryLoad() {
    if (!Shopify.ModelViewerUI) return;
    if (this.modelLoaded) return;

    this.el.querySelectorAll('[data-media-type="model"]').forEach(figure => {
      const controls = ['zoom-in', 'zoom-out'];
      const { productGalleryFigure } = figure.dataset;

      if (document.fullscreenEnabled) controls.push('fullscreen');

      const onPlayListener = () => {
        if (this.flickity) {
          this.flickity.unbindDrag();
        }
      };

      const onPauseListener = () => {
        if (this.flickity) {
          this.flickity.bindDrag();
        }
      };

      const modelViewer = figure.querySelector('model-viewer');

      if (modelViewer) {
        const player = new Shopify.ModelViewerUI(figure.querySelector('model-viewer'), { controls });

        this.models[productGalleryFigure] = player;

        this.media.push({
          type: 'model',
          figure,
          player,
          focus: () => {},
          restart: () => {},
          pause: () => player.pause(),
          play: () => player.play(),
          unload: () => {},
        });

        if (this.flickity) {
          this.events.register(modelViewer, 'shopify_model_viewer_ui_toggle_play', onPlayListener);
          this.events.register(modelViewer, 'shopify_model_viewer_ui_toggle_pause', onPauseListener);
          this.flickity.resize();
        }
      }
    });
    if (this.productMasonryLayout) {
      this.productMasonryLayout.layout();
    }

    this.modelLoaded = true;
  }

  _onViewInYourSpaceClick(target) {
    if (target.dataset.shopifyModel3dId === this.selected.figure.dataset.media) return;

    const figure = this.viewport.querySelector(`[data-media="${target.dataset.shopifyModel3dId}"]`);
    this._selectMediaByEl(figure);
  }

  _selectMediaByEl(el) {
    this._selectMediaByIndex(parseInt(el.dataset.productGalleryFigure, 10));
  }

  _onVideoLibraryLoad() {
    if (!Shopify.Plyr) return;
    if (this.videoLoaded && this.settings.isQuickshop) return;

    const videoFigures = this.el.querySelectorAll('[data-media-type="video"]');

    for (let i = 0; i < videoFigures.length; i++) {
      const videoFigure = videoFigures[i];
      const player = new Shopify.Plyr(
        videoFigure.querySelector('video'),
        {
          loop: { active: this.settings.enable_video_looping },
        },
      );

      const plyrEl = videoFigure.querySelector('.plyr');

      const video = {
        type: 'video',
        figure: videoFigure,
        player,
        focus: () => plyrEl.focus(),
        restart: () => {
          player.restart();
          player.play();
        },
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => player.destroy(),
      };

      this.events.register(videoFigure, 'play', () => this._pauseMedia(video));

      this.media.push(video);

      if (this.flickity) {
        this.flickity.resize();
      }
    }

    if (this.productMasonryLayout && this.settings.isQuickshop) {
      setTimeout(() => {
        this.productMasonryLayout.layout();
      }, 100);
    } else if (this.productMasonryLayout && !this.settings.isQuickshop) {
      this.productMasonryLayout.layout();
    }

    this.videoLoaded = true;
  }

  _onExternalVideoLibraryLoad() {
    if (this.externalVideoLoaded && this.settings.isQuickshop) return;

    const videoFigures = this.el.querySelectorAll('figure[data-media-type="external_video"]');

    for (let i = 0; i < videoFigures.length; i++) {
      const videoFigure = videoFigures[i];
      const videoIframe = videoFigure.querySelector('iframe');
      const iframeSrc = videoIframe.src;
      let video;

      if (iframeSrc.includes('youtube')) {
        const player = new YT.Player(videoIframe);

        video = {
          type: 'video',
          figure: videoFigure,
          player,
          focus: () => player.getIframe().focus(),
          restart: () => {
            player.seekTo(0);
            player.playVideo();
          },
          pause: () => player.pauseVideo(),
          play: () => {
            videoFigure.classList.add('video-playing');

            player.playVideo();
          },
          unload: () => {},
        };

        this.events.register(player, 'onStateChange', ({ data }) => {
          if (data === YT.PlayerState.ENDED && this.settings.enable_video_looping) {
            video.restart();
          } else if (data === YT.PlayerState.PLAYING) {
            this._pauseMedia(video);
          }
        });
      } else {
        const player = new Vimeo(videoIframe);
        player.setLoop(this.settings.enable_video_looping);

        video = {
          type: 'video',
          figure: videoFigure,
          player,
          focus: () => videoIframe.focus(),
          restart: () => {
            player.setCurrentTime(0);
            player.play();
          },
          pause: () => player.pause(),
          play: () => player.play(),
          unload: () => player.unload(),
        };
      }
      this.media.push(video);

      if (this.flickity) {
        this.flickity.resize();
      }
    }
    if (this.productMasonryLayout && this.settings.isQuickshop) {
      setTimeout(() => {
        this.productMasonryLayout.layout();
      }, 100);
    } else if (this.productMasonryLayout && !this.settings.isQuickshop) {
      this.productMasonryLayout.layout();
    }

    this.externalVideoLoaded = true;
  }

  _onMediaSelect(media) {
    if (!media) return;

    switch (media.type) {
      case 'video':
        media.focus();
        if (
          this.settings.enable_video_autoplay
          && !themeUtils.isSmall()
          && this.layout !== 'list'
          && this.layout !== 'masonry'
        ) {
          media.play();
        }
        break;
      case 'model':
        if (document.querySelector('html').classList.contains('no-touch')) {
          media.play();
        }
        break;
      default:
        break;
    }
  }

  _pauseMedia(excludeMedia = null) {
    this.media.forEach(m => {
      if (m !== excludeMedia) {
        m.pause();
      }
    });
  }

  _selectMediaByIndex(index) {
    if (this.layout !== 'slideshow') return;

    const figure = this.figures[index];

    this.selected.figure.dataset.productGallerySelected = false;
    this.selected.figure.setAttribute('aria-hidden', true);
    this.selected.figure = figure;
    this.selected.figure.dataset.productGallerySelected = true;
    this.selected.figure.setAttribute('aria-hidden', false);

    if (this.navigation) {
      const thumbnail = this.thumbnails[index];

      this.selected.thumbnail.dataset.productGallerySelected = false;
      this.selected.thumbnail = thumbnail;
      this.selected.thumbnail.dataset.productGallerySelected = true;
    }

    this._pauseMedia();
    this._onMediaSelect(this.media.filter(m => m.figure === this.selected.figure)[0]);
    if (this.zoomView && figure.dataset.mediaType === 'image') {
      this.zoomView.prepareZoom(true);
    }
  }

  _initProductMasonry(e) {
    if (e && document.fullscreenElement) {
      return;
    } else if (e && !document.fullscreenElement) {
      if (this.productMasonryLayout) {
        this.productMasonryLayout.layout();
      }
    }

    if (themeUtils.isSmall()) {
      if (this.productMasonryLayout) {
        this.unload();
        this.el.setAttribute('style', '');
        this._registerEvents();
      }

      if (!this.flickity) {
        this._initProductFlickity();
      }
    } else {
      if (this.flickity) {
        this.unload();
        this.el.setAttribute('style', '');
        this._registerEvents();
      }

      if (!this.productMasonryLayout) {
        if (this.figures.length > 1) {
          this.productMasonryLayout = new MasonryGrid({
            el: this.el,
            settings: {
              itemSelector: '.product-gallery--viewport--figure',
              columnWidth: '[data-masonry-image-sizer]',
            },
          });
        }

        this._onVideoLibraryLoad();
        this._onExternalVideoLibraryLoad();
        this._onModelLibraryLoad();
      }

      if (this.productMasonryLayout && this.settings.isQuickshop) {
        setTimeout(() => {
          this.productMasonryLayout.layout();
        }, 100);
      } else if (this.productMasonryLayout && !this.settings.isQuickshop ) {
        this.productMasonryLayout.layout();
      }
    }
  }

  _selectFlickitySlide(el) {
    if (el.hasAttribute('data-mobile-slider-button')) {
      if (el.dataset.mobileSliderButton === 'next') {
        this.flickity.next();
      } else {
        this.flickity.previous();
      }
    }

    if (el.hasAttribute('data-mobile-slider-dot')) {
      this.flickity.select(parseInt(el.dataset.slideDotIndex, 10));
    }

    this._pauseMedia();
    this._onMediaSelect(this.media.filter(m => m.figure === this.flickity.selectedElement)[0]);
  }

  _selectFlickityNav() {
    this.pageDots.forEach(dotEl => {
      if (parseInt(dotEl.dataset.slideDotIndex, 10) === this.flickity.selectedIndex) {
        dotEl.classList.add('selected');
      } else {
        dotEl.classList.remove('selected');
      }
    });

    this.flickityNavButtons.forEach(navEl => {
      if (navEl.dataset.mobileSliderButton === 'prev' && this.flickity.selectedIndex === 0) {
        navEl.disabled = true;
      } else if (navEl.dataset.mobileSliderButton === 'next' && this.flickity.selectedIndex === this.flickity.slides.length - 1) {
        navEl.disabled = true;
      } else {
        navEl.disabled = false;
      }
    });
  }

  _initProductFlickity() {
    if (this.figures.length <= 1) return;

    if (!this.flickity) {
      this.flickity = new Flickity(
        this.el,
        {
          cellAlign: 'left',
          cellSelector: '.product-gallery--viewport--figure',
          contain: true,
          percentPosition: false,
          prevNextButtons: true,
          adaptiveHeight: true,
          setGallerySize: true,
          pageDots: true,
          lazyLoad: false,
        },
      );

      this.pageDots = this.productContainer.querySelectorAll('[data-mobile-slider-dot]');
      this.flickityNavButtons = this.productContainer.querySelectorAll('[data-mobile-slider-button]');

      this.flickity.on('select', () => this._selectFlickityNav());

      this.flickityNavButtons.forEach(el => {
        this.flickityEvents.push(this.events.register(el, 'click', () => this._selectFlickitySlide(el)));
      });

      this.pageDots.forEach(el => {
        this.flickityEvents.push(this.events.register(el, 'click', () => this._selectFlickitySlide(el)));
      });

      this.flickityEvents.push(this.events.register(this.el, 'rimg:load', () => this.flickity.resize()));

      // Flickity controls the pointerdown event which prevents model-viewer-ui from
      // triggering .play() on mouseup. This returns this functionality.
      this.flickity.on('staticClick', (event, pointer, cellElement) => {
        const figure = cellElement;

        if (figure && this.models[figure.dataset.productGalleryFigure]) {
          const model = this.models[figure.dataset.productGalleryFigure];
          model.play();
        }
      });
    }
    this._onVideoLibraryLoad();
    this._onExternalVideoLibraryLoad();
    this._onModelLibraryLoad();
  }
}
